/**
 * complete-the-look.js
 * ------------------------------------------------------------
 * Adds CTL items via theme cart store:
 *   window.theme.cart.store.getState().add(...)
 *
 * Includes:
 * - Queue adds (prevents overlapping updates that break after first add)
 * - Always uses current select value for CTL variant id
 * - Re-renders CTL section when MAIN product variant changes
 * - No line item properties
 */

(() => {
  'use strict';

  window.__CTL_LOADED__ = true;
  console.log('[CTL] loaded');

  const SELECTORS = {
    root: '[data-ctl-root]',
    card: '[data-ctl-card]',
    addBtn: '[data-ctl-add]',
    addAllBtn: '[data-ctl-add-all]',
    variantSelect: '[data-ctl-variant-select]',
    msg: '[data-ctl-msg]',
  };

  // ---- Theme store helpers ----
  function getThemeAddFn() {
    return window?.theme?.cart?.store?.getState?.()?.add;
  }

  function waitForThemeCartStore(timeoutMs = 8000) {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const t = setInterval(() => {
        const addFn = getThemeAddFn();
        if (typeof addFn === 'function') {
          clearInterval(t);
          resolve(addFn);
          return;
        }
        if (Date.now() - start > timeoutMs) {
          clearInterval(t);
          reject(new Error('Timed out waiting for theme cart store add()'));
        }
      }, 50);
    });
  }

  async function addViaThemeStore(itemOrItems) {
    const addFn = getThemeAddFn() || (await waitForThemeCartStore());
    return addFn(itemOrItems);
  }

  // ---- Queue ----
  window.__CTL_ADD_QUEUE__ = window.__CTL_ADD_QUEUE__ || Promise.resolve();

  function enqueueAdd(fn) {
    window.__CTL_ADD_QUEUE__ = window.__CTL_ADD_QUEUE__
      .then(() => fn())
      .catch((e) => {
        console.error('[CTL] queued add failed', e);
      });
    return window.__CTL_ADD_QUEUE__;
  }

  // ---- Utilities ----
  function toInt(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  function setMsg(card, text = '') {
    const el = card?.querySelector(SELECTORS.msg);
    if (el) el.textContent = text;
  }

  function clearMsgLater(card, ms = 2000) {
    const el = card?.querySelector(SELECTORS.msg);
    if (!el) return;

    if (el.dataset.ctlTimer) {
      clearTimeout(Number(el.dataset.ctlTimer));
    }

    const t = setTimeout(() => {
      el.textContent = '';
      el.dataset.ctlTimer = '';
    }, ms);

    el.dataset.ctlTimer = String(t);
  }

  function setBusy(btn, isBusy, busyText = 'ADDING…') {
    if (!btn) return;

    if (!btn.dataset.ctlOriginalText) {
      btn.dataset.ctlOriginalText =
        btn.getAttribute('data-ctl-label') ||
        (btn.textContent || '').trim() ||
        'ADD';
    }

    btn.disabled = !!isBusy;
    btn.dataset.ctlBusy = isBusy ? 'true' : '';
    btn.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    btn.textContent = isBusy ? busyText : btn.dataset.ctlOriginalText;
  }

  function flash(btn, text = 'Added ✓', ms = 800) {
    if (!btn) return;

    if (!btn.dataset.ctlFlashTimer) {
      btn.dataset.ctlFlashTimer = '';
    }

    if (btn.dataset.ctlFlashTimer) {
      clearTimeout(Number(btn.dataset.ctlFlashTimer));
      btn.dataset.ctlFlashTimer = '';
    }

    btn.textContent = text;

    const t = setTimeout(() => {
      btn.textContent = btn.dataset.ctlOriginalText || 'ADD';
      btn.dataset.ctlFlashTimer = '';
    }, ms);

    btn.dataset.ctlFlashTimer = String(t);
  }

  // ---- CTL card variant selection ----
  function getVariantId(btn) {
    const card = btn.closest(SELECTORS.card);

    const sel = card?.querySelector(SELECTORS.variantSelect);
    if (sel?.value) return toInt(sel.value);

    const direct = btn.getAttribute('data-variant-id');
    if (direct) return toInt(direct);

    return null;
  }

  function onVariantSelectChange(e) {
    const select = e.target.closest(SELECTORS.variantSelect);
    if (!select) return;

    const card = select.closest(SELECTORS.card);
    const btn = card?.querySelector(SELECTORS.addBtn);
    if (!btn) return;

    btn.setAttribute('data-variant-id', select.value);
    setMsg(card, '');
  }

  // ---- Main product variant helpers ----
  function getMainProductVariantId() {
    const idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (idInput?.value) return toInt(idInput.value);

    const urlVariant = new URLSearchParams(window.location.search).get('variant');
    if (urlVariant) return toInt(urlVariant);

    return null;
  }

  async function fetchSectionHtmlForVariant(sectionId, variantId) {
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('variant', variantId);

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!res.ok) {
      throw new Error(`CTL section fetch failed: ${res.status}`);
    }

    return res.text();
  }

  async function rerenderCtlRoot(root, variantId) {
    if (!root || !variantId) return;

    const sectionId = root.getAttribute('data-section-id');
    if (!sectionId) return;

    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    root.dataset.ctlRequestId = requestId;

    try {
      root.dataset.ctlLoading = 'true';

      const html = await fetchSectionHtmlForVariant(sectionId, variantId);

      if (root.dataset.ctlRequestId !== requestId) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshRoot = doc.querySelector(SELECTORS.root);

      if (!freshRoot) {
        root.innerHTML = '';
        root.hidden = true;
        root.dataset.ctlCurrentParentVariantId = String(variantId);
        return;
      }

      root.innerHTML = freshRoot.innerHTML;
      root.hidden = false;
      root.dataset.ctlCurrentParentVariantId = String(variantId);
    } catch (e) {
      console.error('[CTL] rerender failed', e);
    } finally {
      if (root.dataset.ctlRequestId === requestId) {
        root.dataset.ctlLoading = '';
      }
    }
  }

  function rerenderAllCtlRootsForCurrentVariant() {
    const variantId = getMainProductVariantId();
    if (!variantId) return;

    document.querySelectorAll(SELECTORS.root).forEach((root) => {
      if (root.dataset.ctlCurrentParentVariantId === String(variantId)) return;
      rerenderCtlRoot(root, variantId);
    });
  }

  function bindMainVariantListeners() {
    document.addEventListener(
      'change',
      (e) => {
        const form = e.target.closest('form[action*="/cart/add"]');
        if (!form) return;

        requestAnimationFrame(() => {
          rerenderAllCtlRootsForCurrentVariant();
        });
      },
      true
    );

    const variantIdInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (variantIdInput) {
      let lastValue = variantIdInput.value;

      const observer = new MutationObserver(() => {
        const nextValue = variantIdInput.value;
        if (nextValue !== lastValue) {
          lastValue = nextValue;
          rerenderAllCtlRootsForCurrentVariant();
        }
      });

      observer.observe(variantIdInput, {
        attributes: true,
        attributeFilter: ['value'],
      });

      document.addEventListener(
        'change',
        (e) => {
          const form = e.target.closest('form[action*="/cart/add"]');
          if (!form) return;

          setTimeout(() => {
            if (variantIdInput.value !== lastValue) {
              lastValue = variantIdInput.value;
              rerenderAllCtlRootsForCurrentVariant();
            }
          }, 0);
        },
        true
      );
    }

    window.addEventListener('popstate', rerenderAllCtlRootsForCurrentVariant);

    document.addEventListener('variant:change', () => {
      requestAnimationFrame(rerenderAllCtlRootsForCurrentVariant);
    });

    document.addEventListener('product:variant-change', () => {
      requestAnimationFrame(rerenderAllCtlRootsForCurrentVariant);
    });

    document.addEventListener('theme:variant:change', () => {
      requestAnimationFrame(rerenderAllCtlRootsForCurrentVariant);
    });
  }

  // ---- Add one ----
  function handleAdd(btn) {
    if (!btn || btn.dataset.ctlBusy === 'true') return;

    const card = btn.closest(SELECTORS.card);
    const variantId = getVariantId(btn);

    if (!variantId) {
      console.warn('[CTL] Missing variant id (select value / data-variant-id)');
      setMsg(card, 'Please select a size.');
      return;
    }

    setBusy(btn, true);
    setMsg(card, 'Adding…');

    enqueueAdd(async () => {
      try {
        await addViaThemeStore({ id: Number(variantId), quantity: 1 });
        setMsg(card, 'Added to cart.');
        clearMsgLater(card, 2000);
        flash(btn);
      } catch (e) {
        console.error('[CTL] add failed', e);
        setMsg(card, 'Could not add. Try again.');
        alert('Could not add to cart. Check console.');
      } finally {
        if (btn.dataset.ctlFlashTimer) {
          clearTimeout(Number(btn.dataset.ctlFlashTimer));
          btn.dataset.ctlFlashTimer = '';
        }
        setBusy(btn, false);
      }
    });
  }

  // ---- Add all ----
  function collectAllVariantIds(scope) {
    const ids = [];
    const cards = Array.from(scope.querySelectorAll(SELECTORS.card));

    for (const card of cards) {
      const sel = card.querySelector(SELECTORS.variantSelect);
      const btn = card.querySelector(SELECTORS.addBtn);
      const id = toInt(sel?.value) || toInt(btn?.getAttribute('data-variant-id'));
      if (id) ids.push(id);
    }

    return Array.from(new Set(ids));
  }

  function handleAddAll(btn) {
    if (!btn || btn.dataset.ctlBusy === 'true') return;

    const root = btn.closest(SELECTORS.root) || document;
    const ids = collectAllVariantIds(root);

    if (!ids.length) {
      console.warn('[CTL] No CTL items found to add.');
      return;
    }

    setBusy(btn, true, 'Adding items…');

    enqueueAdd(async () => {
      try {
        await addViaThemeStore(ids.map((id) => ({ id, quantity: 1 })));
        flash(btn, 'Added all ✓');
      } catch (e) {
        console.error('[CTL] add-all failed', e);
        alert('Could not add all items. Check console.');
      } finally {
        setBusy(btn, false);
      }
    });
  }

  // ---- Delegation ----
  function onClick(e) {
    const addBtn = e.target.closest(SELECTORS.addBtn);
    if (addBtn) {
      e.preventDefault();
      handleAdd(addBtn);
      return;
    }

    const addAllBtn = e.target.closest(SELECTORS.addAllBtn);
    if (addAllBtn) {
      e.preventDefault();
      handleAddAll(addAllBtn);
    }
  }

  document.addEventListener('click', onClick, true);
  document.addEventListener('change', onVariantSelectChange, true);
  bindMainVariantListeners();

  // mark current main parent variant on first load
  const initialVariantId = getMainProductVariantId();
  if (initialVariantId) {
    document.querySelectorAll(SELECTORS.root).forEach((root) => {
      root.dataset.ctlCurrentParentVariantId = String(initialVariantId);
    });
  }
})();